import React, { useEffect, useState } from "react";
import TitleStripe from "../../common/TitleStripe";
import {
  calculateRiskScore,
  formatDocument,
  formatToReal,
  getLetterScore,
  getScoreLegends,
  maskCEP,
} from "../../../utils/utils";
import { EvaluationScorePJ } from "../../common/query/EvaluationScorePJ";
import PendenciasFinanceiras from "../../common/query/PendenciasFinanceiras";
import NoOcurrence from "../../common/query/NoOcurrence";
import Protestos from "../../common/query/Protestos";
import ChequesSemFundos from "../../common/query/ChequesSemFundos";
import ChequesSustados from "../../common/query/ChequesSustados";
import AvisoCard from "../../common/query/AvisoCard";
import HistoricoConsultas from "../../common/query/HistoricoConsultas";
import ScoreRating from "./ScoreRating";
import { Badge, Button, Modal, Table } from "flowbite-react";
import { HiOutlineEye, HiThumbDown, HiThumbUp } from "react-icons/hi";
import StreetView from "../../common/query/StreetView";
import ModalReceitaFederal from "../../modals/ModalReceitaFederal";
import moment from "moment";
import { FaRegChartBar, FaUserCheck } from "react-icons/fa";
import { IoReceiptOutline } from "react-icons/io5";
import { LuBuilding2, LuMapPin } from "react-icons/lu";
import {
  GrDocumentExcel,
  GrDocumentText,
  GrDocumentTime,
} from "react-icons/gr";
import { CiMoneyCheck1 } from "react-icons/ci";
import { MdOutlineBalance } from "react-icons/md";
import { HiOutlineCurrencyDollar } from "react-icons/hi";
import { FiUserCheck, FiUsers } from "react-icons/fi";

const calculateCompanyAgeWithMoment = (dateStr) => {
  if (!dateStr) return null;

  // tenta vários formatos comuns
  const foundation = moment(
    dateStr,
    ["DD/MM/YYYY", "YYYY-MM-DD", "YYYYMMDD"],
    true
  );
  if (!foundation.isValid()) return null;

  const now = moment();

  const years = now.diff(foundation, "years");
  const afterYears = foundation.clone().add(years, "years");
  const months = now.diff(afterYears, "months");

  if (years < 0) return null;

  const anosLabel = years > 0 ? `${years} ano${years > 1 ? "s" : ""}` : null;
  const mesesLabel =
    months > 0 ? `${months} mês${months > 1 ? "es" : ""}` : null;

  if (anosLabel && mesesLabel)
    return `${anosLabel} e ${mesesLabel} de atividade`;
  if (anosLabel) return `${anosLabel} de atividade`;
  if (mesesLabel) return `${mesesLabel} de atividade`;

  return "menos de 1 mês de atividade";
};

const formatYYYYMMToYY = (yyyyMM) => {
  if (!yyyyMM) return "–";
  const [y, m] = yyyyMM.split("-");
  return `${m}/${String(y).slice(-2)}`;
};

const formatPeriodoLabel = (periodo) => {
  if (!periodo) return "–";
  if (typeof periodo === "string" && periodo.includes("-")) {
    return formatYYYYMMToYY(periodo);
  }
  return periodo;
};

const getPeriodoFromEntry = (entry) =>
  entry?.periodo ?? entry?.mes ?? entry?.mes_ano ?? entry?.periodo_consulta;

const firstNonEmptySeries = (src, keys) => {
  for (const k of keys) {
    const arr = src?.[k];
    if (Array.isArray(arr) && arr.length) return arr;
  }
  return [];
};

const DefineRiscoQuery = ({ data, dataFull }) => {
  const [riskScore, setRiskScore] = useState(0);
  const [openMap, setOpenMap] = useState(false);
  const companyAgeLabel = calculateCompanyAgeWithMoment(
    data?.dadosCadastrais?.dataFundacao
  );

  useEffect(() => {
    setRiskScore(
      calculateRiskScore(
        +data?.resumoConsulta?.pendenciasFinanceiras?.quantidadeTotal || 0,
        +data?.resumoConsulta?.protestos?.quantidadeTotal || 0,
        +data?.score?.score,
        +data?.score?.probabilidade
      )
    );
  }, [data]);

  const fullAddress = [
    data?.dadosCadastrais?.endereco,
    data?.dadosCadastrais?.bairro,
    data?.dadosCadastrais?.cidade && data?.dadosCadastrais?.uf
      ? `${data?.dadosCadastrais?.cidade} - ${data?.dadosCadastrais?.uf}`
      : data?.dadosCadastrais?.cidade || data?.dadosCadastrais?.uf,
    maskCEP(data?.dadosCadastrais?.cep),
  ]
    .filter(Boolean)
    .join(", ");

  const statusConsumidor = data?.statusConsumidor;
  const statusConsumidorContainsNao = statusConsumidor
    ? statusConsumidor.toUpperCase().includes("NÃO") ||
      statusConsumidor.toUpperCase().includes("CANCELADA") ||
      statusConsumidor.toUpperCase().includes("NAO") ||
      statusConsumidor.toUpperCase().includes("SEM")
    : false;

  const compromissosSeries = data?.comprometimentoFuturo?.compromissos ?? [];
  const creditosSeries = data?.comprometimentoFuturo?.credito ?? [];
  const periodosSeries = [
    ...new Set(
      [
        ...compromissosSeries.map((pt) => pt.periodo).filter(Boolean),
        ...creditosSeries.map((pt) => pt.periodo).filter(Boolean),
      ].filter(Boolean)
    ),
  ];
  const getCompromissoSerie = (periodo) =>
    compromissosSeries.find((item) => item.periodo === periodo);
  const getCreditoSerie = (periodo) =>
    creditosSeries.find((item) => item.periodo === periodo);
  const formatCompromissoValor = (valor) => {
    const parsed = parseFloat(valor);
    if (isNaN(parsed)) {
      return "–";
    }
    if (parsed === 0) {
      return "< 10";
    }
    return parsed * 100;
  };
  const formatCreditoValor = (valor) => {
    const parsed = parseFloat(valor);
    return !isNaN(parsed) ? formatToReal(parsed * 1000) : "–";
  };
  const creditoObtidoSeries = data?.creditoObtido?.series ?? [];
  const compromissosOperacoesSeries = data?.compromissos?.series ?? [];
  const periodosOperacoes = [
    ...new Set(
      [
        ...creditoObtidoSeries
          .map((item) => getPeriodoFromEntry(item))
          .filter(Boolean),
        ...compromissosOperacoesSeries
          .map((item) => getPeriodoFromEntry(item))
          .filter(Boolean),
      ].filter(Boolean)
    ),
  ];
  const getCreditoObtidoPorPeriodo = (periodo) =>
    creditoObtidoSeries.find((item) => getPeriodoFromEntry(item) === periodo);
  const getCompromissosPorPeriodo = (periodo) =>
    compromissosOperacoesSeries.find(
      (item) => getPeriodoFromEntry(item) === periodo
    );
  const getValorTitulosPorPeriodo = (periodo) => {
    const creditoValor = getCreditoObtidoPorPeriodo(periodo)?.valor;
    if (creditoValor != null && creditoValor !== "") {
      return creditoValor;
    }
    return getCompromissosPorPeriodo(periodo)?.valor;
  };
  const formatCreditoObtidoValor = (valor) => {
    const parsed = parseFloat(valor);
    return !isNaN(parsed) ? formatToReal(parsed * 1000) : "–";
  };
  const situacaoCnpj = data?.dadosCadastrais?.situacao || "";
  const situacaoIsAtivo = situacaoCnpj.toLowerCase() === "ativo";

  return (
    <div>
      <TitleStripe>
        <div className="flex items-center gap-2">
          <LuBuilding2 className="text-blue-700" />{" "}
          <span>Dados Cadastrais</span>
        </div>
      </TitleStripe>

      <div className="mt-4  text-xs">
        <div className="flex flex-wrap items-start justify-between gap-2">
          <div>
            <p className="text-[10px] uppercase tracking-[0.2em] text-gray-400">
              Razão Social
            </p>
            <p className="text-lg font-semibold text-gray-900">
              {data?.dadosCadastrais?.razaoSocial || "Indisponível"}
            </p>
            <p className="text-[11px] text-gray-500">
              {data?.dadosCadastrais?.nomeFantasia || "Sem nome fantasia"}
            </p>
          </div>
          <ModalReceitaFederal cnpj={data?.dadosCadastrais?.cnpj} />
        </div>

        <div className="mt-2 flex flex-wrap gap-2 text-[10px]">
          <span className="inline-flex items-center rounded-full border border-gray-200 bg-slate-50 px-2 py-0.5 text-gray-600">
            {situacaoCnpj || "Situação desconhecida"}
          </span>
          <span className="inline-flex items-center rounded-full border border-gray-200 bg-slate-50 px-2 py-0.5 text-gray-600">
            Fundação: {data?.dadosCadastrais?.dataFundacao || "—"}
          </span>
          {companyAgeLabel && (
            <span className="inline-flex items-center rounded-full border border-gray-200 bg-slate-50 px-2 py-0.5 text-gray-600">
              {companyAgeLabel}
            </span>
          )}
        </div>

        <div className="mt-3 grid gap-2 grid-cols-2">
          {[
            {
              label: "CNPJ",
              value: data?.dadosCadastrais?.cnpj
                ? formatDocument(data?.dadosCadastrais?.cnpj)
                : "Não disponível",
              hint: "Documento",
            },
            {
              label: "CNAE",
              value: data?.dadosCadastrais?.ramoAtividade || "Não informado",
              hint: "Atividade",
            },
            {
              label: "Natureza",
              value: data?.dadosCadastrais?.naturezaJuridica || "Não informado",
              hint: "Perfil jurídico",
            },
            {
              label: "Cidade / UF",
              value: `${data?.dadosCadastrais?.cidade || "Não informado"} / ${
                data?.dadosCadastrais?.uf || "--"
              }`,
              hint: "Localização",
            },
          ].map((item) => (
            <div
              key={item.label}
              className="rounded-xl border border-gray-200 bg-slate-50 px-3 py-2"
            >
              <p className="text-[10px] uppercase tracking-[0.2em] text-gray-400">
                {item.label}
              </p>
              <p className="text-base font-semibold text-gray-900">
                {item.value}
              </p>
              <p className="text-[10px] text-gray-500">{item.hint}</p>
            </div>
          ))}
        </div>

        <TitleStripe className="mb-4">
          <div className="flex items-center gap-2">
            <LuMapPin className="text-blue-700" /> <span>Localização</span>
          </div>
        </TitleStripe>
        <div className="grid gap-2 grid-cols-2">
          <div className="rounded-xl border border-gray-200 bg-white px-3 py-2">
            <p className="text-[10px] uppercase tracking-[0.2em] text-gray-400">
              Endereço
            </p>
            <div className="flex items-center gap-2">
              <p className="text-[12px] font-semibold text-gray-900">
                {data?.dadosCadastrais?.endereco || "Não informado"}
              </p>
              {fullAddress && (
                <div
                  className=" flex items-center gap-1 text-[11px] text-blue-600 cursor-pointer"
                  onClick={() => setOpenMap(true)}
                >
                  <span>- Ver fotos do local</span>
                  <HiOutlineEye className="text-base" />
                </div>
              )}
            </div>
          </div>
          <div className="rounded-xl border border-gray-200 bg-white px-3 py-2">
            <p className="text-[10px] uppercase tracking-[0.2em] text-gray-400">
              Bairro · CEP
            </p>
            <p className="text-[12px] font-semibold text-gray-900 flex flex-wrap gap-1">
              <span>{data?.dadosCadastrais?.bairro || "—"}</span>
              <span>·</span>
              <span>{maskCEP(data?.dadosCadastrais?.cep) || "--"}</span>
            </p>
          </div>
        </div>

        <div className="mt-2 grid gap-2 grid-cols-2">
          <div className="rounded-xl border border-gray-200 bg-white px-3 py-2">
            <p className="text-[10px] uppercase tracking-[0.2em] text-gray-400">
              Cidade
            </p>
            <p className="text-[12px] font-semibold text-gray-900">
              {data?.dadosCadastrais?.cidade || "—"}
            </p>
          </div>
          <div className="rounded-xl border border-gray-200 bg-white px-3 py-2">
            <p className="text-[10px] uppercase tracking-[0.2em] text-gray-400">
              UF
            </p>
            <p className="text-[12px] font-semibold text-gray-900">
              {data?.dadosCadastrais?.uf || "--"}
            </p>
          </div>
        </div>
      </div>

      <TitleStripe className="mb-4">
        <div className="flex items-center gap-2">
          <FaRegChartBar className="text-blue-700" />{" "}
          <span>Avaliação preliminar de crédito</span>
        </div>
      </TitleStripe>

      {data?.score?.score == 2 ? (
        <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg text-yellow-800 text-sm">
          <p className="font-medium">
            Não foi possível calcular o score desta empresa.
          </p>
          <p className="mt-1">
            É necessário verificar as outras informações disponíveis no
            relatório para uma análise completa.
          </p>
        </div>
      ) : (
        <EvaluationScorePJ
          data={data}
          score={data?.score?.score}
          probability={+data?.score?.probabilidade * 100}
          type="CNPJ"
          hasIncome={false}
        />
      )}

      {dataFull?.scoreRating?.statusRetorno == 1 && (
        <ScoreRating score={dataFull?.scoreRating} block={false} />
      )}

      {statusConsumidor && (
        <div className="mt-5 w-full">
          <div
            className={`flex items-center justify-between gap-4 rounded-2xl border px-4 py-4 text-sm shadow-sm ${
              statusConsumidorContainsNao
                ? "border-rose-200 bg-rose-50/70 text-rose-700"
                : "border-emerald-200 bg-emerald-50/70 text-emerald-700"
            }`}
          >
            <div className="flex items-center gap-3">
              <div
                className={`flex h-10 w-10 items-center justify-center rounded-full border ${
                  statusConsumidorContainsNao
                    ? "border-rose-200 bg-rose-100 text-rose-700"
                    : "border-emerald-200 bg-emerald-100 text-emerald-700"
                }`}
              >
                {statusConsumidorContainsNao ? (
                  <HiThumbDown className="h-5 w-5" />
                ) : (
                  <HiThumbUp className="h-5 w-5" />
                )}
              </div>
              <div>
                <div className="text-[10px] font-semibold uppercase tracking-[0.25em] opacity-70">
                  Status do consumidor
                </div>
                <div className="text-base font-semibold text-slate-900">
                  {statusConsumidor}
                </div>
              </div>
            </div>
            <div className="hidden rounded-full border border-white/40 bg-white/30 px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.2em] sm:block">
              {statusConsumidorContainsNao ? "Atenção" : "Regular"}
            </div>
          </div>
        </div>
      )}

      {!statusConsumidorContainsNao && (
        <>
          {data?.pagamentoPontual &&
            data?.pagamentoPontual?.series?.length > 0 && (
              <div>
                <TitleStripe className="mb-4">Pagamentos Pontuais</TitleStripe>
                {data?.pagamentoPontual ? (
                  <>
                    <div className="mt-3 grid gap-3">
                      <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white">
                        <div className="border-b border-slate-200 bg-slate-50 px-4 py-2 text-[10px] font-semibold uppercase tracking-[0.28em] text-slate-500">
                          Pagamentos pontuais por período (últimos 12 meses)
                        </div>
                        <div className="overflow-x-auto">
                          <table className="min-w-full">
                            <thead>
                              <tr>
                                {data?.pagamentoPontual?.series?.map(
                                  (pt, idx) => (
                                    <th
                                      key={idx}
                                      className="px-2 py-2 text-center text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-500"
                                    >
                                      {formatYYYYMMToYY(pt.mes)}
                                    </th>
                                  )
                                )}
                              </tr>
                            </thead>
                            <tbody>
                              <tr className="border-t border-slate-100">
                                {data?.pagamentoPontual?.series?.map(
                                  (pt, idx) => {
                                    const n = parseFloat(pt?.valor);
                                    return (
                                      <td
                                        key={idx}
                                        className="px-2 py-3 text-center text-sm font-semibold text-slate-700 tabular-nums"
                                      >
                                        {!isNaN(n) ? `${n}%` : "–"}
                                      </td>
                                    );
                                  }
                                )}
                              </tr>
                            </tbody>
                          </table>
                        </div>
                      </div>

                      <div className="flex flex-wrap items-center justify-center gap-3 text-xs text-slate-500">
                        <span className="inline-flex items-center gap-2">
                          <span className="h-2 w-2 rounded-full bg-emerald-500" />
                          100%: Pagamentos pontuais
                        </span>
                        <span className="text-slate-300">|</span>
                        <span className="inline-flex items-center gap-2">
                          <span className="h-2 w-2 rounded-full bg-rose-500" />
                          0%: Pagamentos não pontuais
                        </span>
                      </div>
                    </div>
                  </>
                ) : (
                  <NoOcurrence />
                )}
              </div>
            )}

          {data?.pagamentoAtrasado &&
            data?.pagamentoAtrasado?.atraso_medio_dias?.length > 0 && (
              <div>
                <TitleStripe className="mb-4">
                  Pagamentos em atrasos
                </TitleStripe>

                {data?.pagamentoAtrasado ? (
                  <>
                    <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white">
                      <div className="border-b border-slate-200 bg-slate-50 px-4 py-2 text-[10px] font-semibold uppercase tracking-[0.28em] text-slate-500">
                        Pagamentos em atraso por período (últimos 12 meses)
                      </div>
                      <div className="overflow-x-auto">
                        <table className="min-w-full">
                          <thead>
                            <tr>
                              <th className="px-3 py-2 text-left text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-500">
                                Tipo
                              </th>
                              {firstNonEmptySeries(data?.pagamentoAtrasado, [
                                "dias_6_15",
                                "dias_16_30",
                                "dias_31_60",
                                "mais_60",
                                "atraso_medio_dias",
                              ])?.map((pt, idx) => (
                                <th
                                  key={idx}
                                  className="px-2 py-2 text-center text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-500"
                                >
                                  {formatYYYYMMToYY(pt.mes)}
                                </th>
                              ))}
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-slate-100">
                            {[
                              ["Período 6 a 15 dias", "dias_6_15"],
                              ["Período 16 a 30 dias", "dias_16_30"],
                              ["Período 31 a 60 dias", "dias_31_60"],
                              ["Mais de 60 dias", "mais_60"],
                              ["Atraso médio (dias)", "atraso_medio_dias"],
                            ].map(([label, key], i) => {
                              const series =
                                data?.pagamentoAtrasado?.[key] || [];
                              if (!Array.isArray(series) || series.length === 0)
                                return null;

                              return (
                                <tr key={i}>
                                  <td className="px-3 py-3 text-left text-2xs font-medium text-slate-700">
                                    {label}
                                  </td>
                                  {series.map((pt, idx) => {
                                    const n = parseFloat(pt?.valor);
                                    const isDias = key === "atraso_medio_dias";
                                    return (
                                      <td
                                        key={idx}
                                        className="px-2 py-3 text-center text-2xs font-semibold text-slate-700 tabular-nums"
                                      >
                                        {!isNaN(n)
                                          ? isDias
                                            ? `${n}d`
                                            : `${n}%`
                                          : "–"}
                                      </td>
                                    );
                                  })}
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    </div>

                    <div className="mt-3 flex flex-wrap items-center justify-center gap-3 text-xs text-slate-500">
                      <span className="inline-flex items-center gap-2">
                        <span className="h-2 w-2 rounded-full bg-rose-500" />
                        100%: Pagamentos em atrasos
                      </span>
                      <span className="text-slate-300">|</span>
                      <span className="inline-flex items-center gap-2">
                        <span className="h-2 w-2 rounded-full bg-emerald-500" />
                        0%: Pagamentos em dia
                      </span>
                    </div>
                  </>
                ) : (
                  <NoOcurrence />
                )}
              </div>
            )}

          {periodosOperacoes.length > 0 && (
            <div>
              <TitleStripe className="mb-4">
                Compromissos e valor dos títulos
              </TitleStripe>

              <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white">
                <div className="border-b border-slate-200 bg-slate-50 px-4 py-2 text-[10px] font-semibold uppercase tracking-[0.28em] text-slate-500">
                  Total de obrigações financeiras por período (últimos 12 meses)
                </div>
                <div className="overflow-x-auto">
                  <table className="min-w-full">
                    <thead>
                      <tr>
                        <th className="px-3 py-2 text-left text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-500">
                          Indicador
                        </th>
                        {periodosOperacoes.map((periodo) => (
                          <th
                            key={periodo}
                            className="px-2 py-2 text-center text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-500"
                          >
                            {formatPeriodoLabel(periodo)}
                          </th>
                        ))}
                      </tr>
                    </thead>

                    <tbody className="divide-y divide-slate-100">
                      <tr>
                        <td className="px-3 py-3 text-left text-xs font-medium text-slate-700">
                          Qtd. de títulos
                        </td>
                        {periodosOperacoes.map((periodo) => (
                          <td
                            key={`compromissos-${periodo}`}
                            className="px-2 py-3 text-center text-xs font-semibold text-slate-700 tabular-nums"
                          >
                            {formatCompromissoValor(
                              getCompromissosPorPeriodo(periodo)?.valor
                            )}
                          </td>
                        ))}
                      </tr>
                      <tr>
                        <td className="px-3 py-3 text-left text-xs font-medium text-slate-700">
                          Valor dos títulos
                        </td>
                        {periodosOperacoes.map((periodo) => (
                          <td
                            key={`credito-obtido-${periodo}`}
                            className="px-2 py-3 text-center text-2xs font-semibold text-slate-700 tabular-nums"
                          >
                            {formatCreditoObtidoValor(
                              getValorTitulosPorPeriodo(periodo)
                            )}
                          </td>
                        ))}
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>

              <div className="mt-3 rounded-xl border border-slate-200 bg-slate-50 p-3 text-sm text-slate-600">
                Descrição: Número total de obrigações financeiras registradas no
                nome do consumidor ou empresa, como boletos, duplicatas, carnês
                ou contratos de crédito. Indica a quantidade de compromissos
                ativos e o total de crédito obtido.
              </div>
            </div>
          )}

          {periodosSeries.length > 0 && (
            <div>
              <TitleStripe className="mb-4">Comprometimento Futuro</TitleStripe>

              <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white">
                <div className="border-b border-slate-200 bg-slate-50 px-4 py-2 text-[10px] font-semibold uppercase tracking-[0.28em] text-slate-500">
                  Comprometimento Futuro por período (últimos 12 meses)
                </div>
                <div className="overflow-x-auto">
                  <table className="min-w-full">
                    <thead>
                      <tr>
                        <th className="px-3 py-2 text-left text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-500">
                          Indicador
                        </th>
                        {periodosSeries.map((periodo) => (
                          <th
                            key={periodo}
                            className="px-2 py-2 text-center text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-500"
                          >
                            {formatPeriodoLabel(periodo)}
                          </th>
                        ))}
                      </tr>
                    </thead>

                    <tbody className="divide-y divide-slate-100">
                      <tr>
                        <td className="px-3 py-3 text-left text-xs font-medium text-slate-700">
                          Qtd. de títulos
                        </td>
                        {periodosSeries.map((periodo) => (
                          <td
                            key={`compromisso-${periodo}`}
                            className="px-2 py-3 text-center text-xs font-semibold text-slate-700 tabular-nums"
                          >
                            {formatCompromissoValor(
                              getCompromissoSerie(periodo)?.pontuacao
                            )}
                          </td>
                        ))}
                      </tr>
                      <tr>
                        <td className="px-3 py-3 text-left text-xs font-medium text-slate-700">
                          Valor dos títulos
                        </td>
                        {periodosSeries.map((periodo) => (
                          <td
                            key={`credito-${periodo}`}
                            className="px-2 py-3 text-center text-xs font-semibold text-slate-700 tabular-nums"
                          >
                            {formatCreditoValor(
                              getCreditoSerie(periodo)?.pontuacao
                            )}
                          </td>
                        ))}
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>

              {compromissosSeries.length > 0 && (
                <div className="mt-3 rounded-xl border border-slate-200 bg-slate-50 p-3 text-sm text-slate-600">
                  Descrição: Número total de obrigações financeiras registradas
                  no nome do consumidor ou empresa, como boletos, duplicatas,
                  carnês ou contratos de crédito. Indica a quantidade de
                  compromissos ativos.
                </div>
              )}
            </div>
          )}
        </>
      )}

      <div id="negativacoes-base-1">
        <TitleStripe className="mb-4">
          <div className="flex items-center gap-2">
            <HiOutlineCurrencyDollar className="text-blue-700" />
            <span>Negativações Registradas</span>
          </div>
        </TitleStripe>

        {data?.pendenciasFinanceiras?.length > 0 ? (
          <>
            <PendenciasFinanceiras
              quantidadeTotal={
                data?.resumoConsulta?.pendenciasFinanceiras?.quantidadeTotal
              }
              valorTotal={
                data?.resumoConsulta?.pendenciasFinanceiras?.valorTotal
              }
              pendenciasFinanceiras={data?.pendenciasFinanceiras}
            />
          </>
        ) : (
          <NoOcurrence />
        )}
      </div>
      <div id="protestos-base-1">
        <TitleStripe className="mb-4">
          <div className="flex items-center gap-2">
            <GrDocumentExcel className="text-blue-700" />
            <span>Títulos protestados</span>
          </div>
        </TitleStripe>
        {data?.protestos?.length > 0 ? (
          <Protestos
            quantidadeTotal={data?.resumoConsulta?.protestos?.quantidadeTotal}
            valorTotal={data?.resumoConsulta?.protestos?.valorTotal}
            protestos={data?.protestos}
            temVencimento={false}
          />
        ) : (
          <NoOcurrence />
        )}
      </div>

      <div id="ccf-base-1">
        <TitleStripe className="mb-4">
          <div className="flex items-center gap-2">
            <CiMoneyCheck1 className="text-blue-700" />
            <span>Cheques sem fundo</span>
          </div>
        </TitleStripe>
        {data?.chequesSemFundo?.length > 0 ? (
          <>
            <ChequesSemFundos
              quantidadeTotal={data?.chequesSemFundo?.length}
              chequesSemFundos={data?.chequesSemFundo}
            />
          </>
        ) : (
          <NoOcurrence />
        )}
      </div>
      <div id="ccs-base-1">
        <TitleStripe className="mb-4">
          <div className="flex items-center gap-2">
            <CiMoneyCheck1 className="text-blue-700" />
            <span>Cheques sustados</span>
          </div>
        </TitleStripe>
        {data?.chequesSustados?.length > 0 ? (
          <>
            <ChequesSustados
              quantidadeTotal={
                data?.resumoConsulta?.chequesSustados?.quantidadeTotal
              }
              chequesSustados={data?.chequesSustados}
            />
          </>
        ) : (
          <NoOcurrence />
        )}
      </div>

      <div>
        <TitleStripe className="mb-4">
          <div className="flex items-center gap-2">
            <GrDocumentTime className="text-blue-700" />
            <span>Histórico de consultas</span>
          </div>
        </TitleStripe>

        {data?.historicoConsultas?.length > 0 ? (
          <div className="flex flex-col gap-2">
            <div className="flex-1">
              <HistoricoConsultas
                quantidadeTotal={data?.historicoConsultas?.length}
                historicoConsultas={data?.historicoConsultas}
              />
            </div>
            {/* {data?.historicoConsultasResumo?.paresMesAnoHistoricoConsultas
              ?.length > 0 && (
              <ReactApexChart
                className="border border-gray-200 rounded-md mt-3"
                options={{
                  colors: ["var(--primary)"],
                  chart: {
                    height: 150,
                    type: "bar",
                    toolbar: {
                      show: false, // Remove o menu de ações
                    },
                  },
                  plotOptions: {
                    bar: {
                      borderRadius: 2,
                      dataLabels: {
                        position: "center", // top, center, bottom
                      },
                    },
                  },
                  dataLabels: {
                    enabled: true,
                    formatter: function (val) {
                      return val;
                    },
                    offsetY: 0,
                    style: {
                      fontSize: "12px",
                      colors: ["white"],
                    },
                  },

                  xaxis: {
                    categories:
                      data?.historicoConsultasResumo
                        ?.paresMesAnoHistoricoConsultas,
                    position: "top",
                    axisBorder: {
                      show: false,
                    },
                    axisTicks: {
                      show: false,
                    },
                    crosshairs: {
                      fill: {
                        type: "gradient",
                        gradient: {
                          colorFrom: "#30ce2d",
                          colorTo: "#30ce2d",
                          stops: [0, 100],
                          opacityFrom: 0.4,
                          opacityTo: 0.5,
                        },
                      },
                    },
                    tooltip: {
                      enabled: true,
                    },
                  },
                  yaxis: {
                    axisBorder: {
                      show: false,
                    },
                    axisTicks: {
                      show: false,
                    },
                    labels: {
                      show: false,
                      formatter: function (val) {
                        return val;
                      },
                    },
                  },
                  title: {
                    text: "Últimos 12 meses",
                    floating: true,
                    offsetY: 200,
                    align: "center",
                    style: {
                      color: "#444",
                    },
                  },
                }}
                series={[
                  {
                    name: "Qtd. Consultas no período",
                    data: data?.historicoConsultasResumo
                      ?.totaisHistoricoConsultas,
                  },
                ]}
                type="bar"
                height={220}
              />
            )} */}
          </div>
        ) : (
          <NoOcurrence />
        )}
      </div>
      <Modal show={openMap} onClose={() => setOpenMap(false)} size="5xl">
        <Modal.Header>Localização</Modal.Header>
        <Modal.Body>
          {fullAddress ? (
            <StreetView address={fullAddress} height={460} />
          ) : (
            <div className="p-3 text-sm text-gray-500">
              Endereço indisponível.
            </div>
          )}
        </Modal.Body>
      </Modal>
    </div>
  );
};

export default DefineRiscoQuery;
